import { useState, useEffect } from 'react';
import { Card, Button, Row, Col, ListGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

export default function PollContent({ pollId, getPolls }) {
  const [poll, setPoll] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);

  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector((x) => x.auth);

  console.log('PollId', pollId);

  // Fetch poll data
  const fetchPoll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch poll: ${response.statusText}`);
      }

      const data = await response.json();
      setPoll(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPoll();
    // Check if the user has already voted for this poll
    const votedPolls = JSON.parse(localStorage.getItem('votedPolls')) || [];
    if (votedPolls.includes(pollId)) {
      setHasVoted(true);
    }
    // eslint-disable-next-line
  }, [pollId, url]);

  const handleVote = async () => {
    if (!selectedOptionId) {
      toast.error('Please select an option before voting.', { position: 'bottom-left' });
      return;
    }

    try {
      // Fetch the latest poll data to verify the status
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch poll status: ${response.statusText}`);
      }

      const latestPoll = await response.json();

      // Check if the poll status is still 'active'
      if (latestPoll.status !== 'active') {
        toast.warning('The vote cannot be cast. Vote session is not active. Please reload the poll.', { position: 'bottom-left' });
        return;
      }

      // Proceed with submitting the vote
      const votedPolls = JSON.parse(localStorage.getItem('votedPolls')) || [];
      const expiryTime = 10 * 60 * 1000; // 10 minutes in milliseconds
      const now = Date.now();

      // Remove expired polls
      const validPolls = votedPolls.filter((poll) => now < poll.expiry);
      localStorage.setItem('votedPolls', JSON.stringify(validPolls));

      // Check if the current poll is already voted
      if (!validPolls.some((poll) => poll.id === pollId)) {
        const voteResponse = await fetch(`${url}/vote/${pollId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            // 'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({ optionId: selectedOptionId }),
        });

        if (!voteResponse.ok) {
          throw new Error(`Failed to submit vote: ${voteResponse.statusText}`);
        }

        validPolls.push({ id: pollId, expiry: now + expiryTime });
        localStorage.setItem('votedPolls', JSON.stringify(validPolls));
        setHasVoted(true);
        toast.success('Your vote has been submitted!', { position: 'bottom-left' });

        // Optionally, reload the poll data to reflect the updated votes
        fetchPoll();
      } else {
        toast.info('You have already voted for this poll.', { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Failed to submit vote: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authUser.token,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete poll: ${response.statusText}`);
      }

      toast.success('Poll deleted successfully', { position: 'bottom-left' });
      getPolls();
    } catch (error) {
      toast.error(`Failed to delete poll: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const handleStatusChange = async () => {
    if (!poll) return;

    // Determine the next status
    let nextStatus;
    switch (poll.status) {
      case 'ready':
        nextStatus = 'active';
        break;
      case 'active':
        nextStatus = 'closed';
        break;
      case 'closed':
        nextStatus = 'displayed';
        break;
      default:
        toast.error('Invalid poll status', { position: 'bottom-left' });
        return;
    }

    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify({ status: nextStatus }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update poll status: ${response.statusText}`);
      }

      const updatedPoll = await response.json();
      setPoll(updatedPoll); // Update the poll state with the new status
      toast.success(`Poll ${updatedPoll.title} status updated to "${nextStatus}"`, { position: 'bottom-left' });
      getPolls(); // Refresh the list of polls
    } catch (error) {
      console.error(`An error occurred while updating the poll status: ${error.message}`);
      toast.error(`Failed to update poll status: ${error.message}`, { position: 'bottom-left' });
    }
  };

  // // Determine if the user can view the poll
  // const canViewPoll = () => {
  //   if (!poll || !authUser) return false;

  //   // Poll is visible to all signed-in users when status is 'active' or 'displayed'
  //   if (poll.status === 'active' || poll.status === 'displayed') {
  //     return true;
  //   }

  //   // Poll is visible to officers
  //   const isOfficer = authUser.acl === 'Officer';

  //   return isOfficer;
  // };

  useEffect(() => {
    if (poll && pollId) {
      fetchPoll();
    }
    // eslint-disable-next-line
  }, [poll?.status]);

  if (isLoading) {
    return <p>Loading poll...</p>;
  }

  if (error) {
    return <p className="text-danger">Error: {error}</p>;
  }

  if (!poll) {
    return <p>No poll data available.</p>;
  }

  // Always render the poll card, but only allow selection when poll.status === 'active'
  return (
    <Card className="mt-3 mb-3">
      <Card.Header>
        <Row>
          <Col xs={12}>
            {poll.title}
            {poll.status === 'ready' && <span className="badge bg-success ms-2">{poll.status}</span>}
            {poll.status === 'active' && <span className="badge bg-warning ms-2">{poll.status}</span>}
            {poll.status === 'closed' && <span className="badge bg-danger ms-2">{poll.status}</span>}
            {poll.status === 'displayed' && <span className="badge bg-info ms-2">{poll.status}</span>}
          </Col>
        </Row>
      </Card.Header>
      <>
        <Card.Body>
          <Row className="mb-2 mt-2">
            <Col>
              <Button
                variant="primary"
                className="float-end"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  fetchPoll(); // Reload the poll data
                  toast.info('Poll reloaded successfully', { position: 'bottom-left' });
                }}
              >
                Reload <i className="bi bi-arrow-clockwise"></i>
              </Button>
            </Col>
          </Row>

          {hasVoted && poll.status === 'active' ? (
            <p className="text-center mt-3">
              Thank you for your vote! The results will be announced later.
            </p>
          ) : poll.status === 'ready' || poll.status === 'active' || poll.status === 'closed' ? (
            <>
              <ListGroup data-bs-theme={'dark'}>
                <Row className="justify-content-center">
                  {poll.options.map((option, index) => (
                    <Col
                      xs={12}
                      md={6}
                      lg={4}
                      key={index}
                      className="mb-2"
                      onClick={() => poll.status === 'active' && setSelectedOptionId(option._id)}
                      style={{ cursor: poll.status === 'active' ? 'pointer' : 'default' }}
                    >
                      <ListGroup.Item active={poll.status === 'active' && selectedOptionId === option._id}>
                        <span style={{ alignItems: 'center', textAlign: 'left', display: 'block' }}>
                          {option.avatar ? (
                            <img
                              src={`data:image/png;base64,${option.avatar}`}
                              alt="avatar"
                              style={{ width: 100, height: 100, borderRadius: '50%', marginRight: 8 }}
                            />
                          ) : (
                            <span
                              style={{
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: '#ccc',
                                display: 'inline-block',
                                marginRight: 8,
                                position: 'relative',
                                textAlign: 'center',
                                lineHeight: '100px',
                              }}
                            >
                              <i className="bi bi-person" style={{ fontSize: 48, color: '#888', verticalAlign: 'middle' }}></i>
                            </span>
                          )}
                          <span style={{ marginLeft: 8, verticalAlign: 'middle', textAlign: 'left', display: 'inline-block' }}>
                            {option.firstName} {option.lastName}
                          </span>
                          {/* Show check only if selected and poll is active */}
                          {poll.status === 'active' && selectedOptionId === option._id && (
                            <i className="bi bi-check-circle-fill ms-2" style={{ color: '#0d6efd', fontSize: 24, verticalAlign: 'middle' }}></i>
                          )}
                        </span>
                      </ListGroup.Item>
                    </Col>
                  ))}
                </Row>
              </ListGroup>
              {poll.status === 'active' &&
                <div className="text-center mt-3">
                  <Button variant="primary" onClick={handleVote}>
                    Submit Vote
                  </Button>
                </div>
              }
            </>
          ) : (<ListGroup data-bs-theme={'dark'}>
            <Row className="justify-content-center">
              {(() => {
                const maxVotes = Math.max(...poll.options.map((opt) => opt.votes));
                const winners = poll.options.filter(option => option.votes === maxVotes && maxVotes > 0);
                return (
                  <>
                    <Col xs={12} className="text-center mb-4">
                      <h1>
                        {winners.length === 1 ? 'Winner' : 'Winners'}
                      </h1>
                    </Col>
                    {winners.map((option, index) => (
                      <Col
                        xs={12}
                        md={6}
                        lg={4}
                        key={index}
                        className="mb-2 d-flex flex-column align-items-center justify-content-center"
                      >
                        {/* Winner Avatar */}
                        {option.avatar ? (
                          <img
                            src={`data:image/png;base64,${option.avatar}`}
                            alt="avatar"
                            style={{ width: 250, height: 250, borderRadius: '50%', objectFit: 'cover', marginBottom: 16 }}
                          />
                        ) : (
                          <span
                            style={{
                              width: 250,
                              height: 250,
                              borderRadius: '50%',
                              background: '#ccc',
                              display: 'inline-block',
                              marginBottom: 16,
                              position: 'relative',
                              textAlign: 'center',
                              lineHeight: '250px',
                            }}
                          >
                            <i className="bi bi-person" style={{ fontSize: 120, color: '#888', verticalAlign: 'middle' }}></i>
                          </span>
                        )}
                        <ListGroup.Item style={{ width: '100%' }}>
                          <Row>
                            <Col>
                              {option.firstName} {option.lastName}
                            </Col>
                            {/* <Col xs={4} className="text-end">
                    <span className="badge ms-2 bg-warning">
                      {option.votes} {option.votes === 1 ? 'vote' : 'votes'}
                    </span>
                  </Col> */}
                          </Row>
                        </ListGroup.Item>
                      </Col>
                    ))}
                  </>
                );
              })()}
            </Row>
          </ListGroup>)}

        </Card.Body>
      </>
      {authUser?.user?.acl && authUser.user.acl === 'Officer' && (
        <Card.Footer className="text-muted">
          {poll.status !== 'displayed' && (
            <Button variant="secondary" onClick={handleStatusChange}>
              {poll.status === 'ready' && 'Activate'}
              {poll.status === 'active' && 'Close'}
              {poll.status === 'closed' && 'Display'}
            </Button>
          )}

          <Button variant="danger" className="float-end" onClick={handleDelete}>
            Delete <i className="bi bi-trash"></i>
          </Button>
        </Card.Footer>
      )}
    </Card>
  );
}